import { z } from 'zod';
import { RETRO_PHASES } from './phases.js';
import { ACTION_STATUSES, VOTE_TARGET_TYPES } from './types.js';
import { retroSettingsSchema } from './settings.js';
import { isAvatar } from './avatars.js';

export const COMMENT_MAX_LENGTH = 1000;
export const GROUP_TITLE_MAX_LENGTH = 80;
export const ACTION_TITLE_MAX_LENGTH = 200;
export const ACTION_DESCRIPTION_MAX_LENGTH = 2000;
export const ROTI_COMMENT_MAX_LENGTH = 1000;
export const DISPLAY_NAME_MAX_LENGTH = 60;

const id = z.string().min(1).max(64);
const trimmedText = (max: number) => z.string().trim().min(1).max(max);

/* ---------------------------------- auth --------------------------------- */

export const registerSchema = z.object({
  name: trimmedText(DISPLAY_NAME_MAX_LENGTH),
  email: z.string().trim().toLowerCase().email().max(254),
  // Length is the only password rule worth enforcing; composition rules push
  // people towards predictable substitutions. The message is shown verbatim by
  // the API, so it is written for a human, not a Zod internals dump.
  password: z.string().min(10, 'Password must be at least 10 characters.').max(200),
  /**
   * A team invite token, when the account is being created by following one.
   *
   * Holding a valid invite *is* the authorisation to have an account, so this is
   * what lets someone sign up on an instance that has closed open registration.
   * Without it, sending a colleague a team invite would be a dead end: they are
   * told to sign in, and cannot.
   */
  inviteToken: z.string().trim().min(1).max(200).optional(),
});

export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
  password: z.string().min(1).max(200),
});

export const updateProfileSchema = z.object({
  name: trimmedText(DISPLAY_NAME_MAX_LENGTH),
  // Constrained to the catalogue rather than accepting any string: the avatar is
  // rendered as text everywhere, and an unbounded field there is a liability for
  // no gain. Empty is allowed and means "show my initial" (see the Avatar
  // component's fallback).
  avatar: z.string().refine((value) => value === '' || isAvatar(value), {
    message: 'Pick one of the available avatars.',
  }),
});

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  // Same rule and message as registration; keep the two in step.
  newPassword: z.string().min(10, 'Password must be at least 10 characters.').max(200),
});

export const resetPasswordSchema = z.object({
  token: z.string().min(1).max(128),
  // Same rule and message as the other password fields; keep all three in step.
  newPassword: z.string().min(10, 'Password must be at least 10 characters.').max(200),
});

export const joinAsGuestSchema = z.object({
  name: trimmedText(DISPLAY_NAME_MAX_LENGTH),
});

/* ---------------------------------- teams -------------------------------- */

export const createTeamSchema = z.object({
  name: trimmedText(80),
});

export const renameTeamSchema = createTeamSchema;

export const inviteMemberSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
});

export const setMemberRoleSchema = z.object({
  role: z.enum(['FACILITATOR', 'CONTRIBUTOR']),
});

/* --------------------------------- retros -------------------------------- */

export const createRetroSchema = z.object({
  teamId: id,
  templateId: id,
  name: trimmedText(120).optional(),
});

/* -------------------------------- templates ------------------------------- */

export const TEMPLATE_NAME_MAX_LENGTH = 80;
export const TEMPLATE_DESCRIPTION_MAX_LENGTH = 200;
export const COLUMN_TITLE_MAX_LENGTH = 60;
export const COLUMN_DESCRIPTION_MAX_LENGTH = 160;

export const MIN_TEMPLATE_COLUMNS = 1;
/**
 * Eight is past the point of usefulness — a board wider than this cannot be read
 * on a laptop, let alone a phone — but it leaves room for formats like Six
 * Thinking Hats without feeling arbitrary.
 */
export const MAX_TEMPLATE_COLUMNS = 8;

export const templateColumnSchema = z.object({
  title: trimmedText(COLUMN_TITLE_MAX_LENGTH),
  description: z.string().trim().max(COLUMN_DESCRIPTION_MAX_LENGTH).default(''),
  emoji: z.string().trim().max(16).default(''),
});

export const upsertTemplateSchema = z.object({
  teamId: id,
  name: trimmedText(TEMPLATE_NAME_MAX_LENGTH),
  description: z.string().trim().max(TEMPLATE_DESCRIPTION_MAX_LENGTH).default(''),
  columns: z.array(templateColumnSchema).min(MIN_TEMPLATE_COLUMNS).max(MAX_TEMPLATE_COLUMNS),
});

export type TemplateColumnInput = z.infer<typeof templateColumnSchema>;
export type UpsertTemplateInput = z.infer<typeof upsertTemplateSchema>;

export const createTopicSchema = z.object({
  name: trimmedText(40),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Expected a hex colour like #4f46e5'),
});

/* ----------------------------- socket intents ---------------------------- */

export const setReadySchema = z.object({ isReady: z.boolean() });

export const advancePhaseSchema = z.object({
  /** Guards against a double-click advancing two steps. */
  from: z.enum(RETRO_PHASES),
  to: z.enum(RETRO_PHASES),
});

export const updateSettingsSchema = z.object({
  settings: retroSettingsSchema.deepPartial(),
});

export const renameRetroSchema = z.object({ name: trimmedText(120) });

export const removeParticipantSchema = z.object({ participantId: id });

export const startTimerSchema = z.object({
  durationSeconds: z.number().int().min(10).max(60 * 60),
});

export const createCommentSchema = z.object({
  columnId: id,
  text: trimmedText(COMMENT_MAX_LENGTH),
  isAnonymous: z.boolean().default(false),
  gifUrl: z.string().url().max(2048).nullable().optional(),
});

export const updateCommentSchema = z.object({
  commentId: id,
  text: trimmedText(COMMENT_MAX_LENGTH),
});

export const deleteCommentSchema = z.object({ commentId: id });

export const moveCommentSchema = z.object({
  commentId: id,
  columnId: id,
  /** Target position within the column, among ungrouped cards and groups. */
  order: z.number().int().min(0).max(10_000),
});

export const groupCommentsSchema = z.object({
  /** The comment being dragged. */
  commentId: id,
  /** The comment or group it was dropped on. */
  target: z.object({ type: z.enum(VOTE_TARGET_TYPES), id }),
});

export const ungroupCommentSchema = z.object({ commentId: id });

export const renameGroupSchema = z.object({
  groupId: id,
  title: z.string().trim().max(GROUP_TITLE_MAX_LENGTH),
});

export const setCommentTopicsSchema = z.object({
  commentId: id,
  topicIds: z.array(id).max(10),
});

export const toggleReactionSchema = z.object({
  commentId: id,
  emoji: z.string().min(1).max(16),
});

export const toggleGroupReactionSchema = z.object({ groupId: id, emoji: z.string().min(1).max(16) });

export const setVoteSchema = z.object({
  target: z.object({ type: z.enum(VOTE_TARGET_TYPES), id }),
  /** Absolute desired count for this target, not a delta. Idempotent on retry. */
  count: z.number().int().min(0).max(20),
});

export const resolveSuggestionSchema = z.object({
  suggestionId: id,
  accept: z.boolean(),
});

export const dropCommentFromSuggestionSchema = z.object({
  suggestionId: id,
  commentId: id,
});

export const upsertActionItemSchema = z.object({
  actionItemId: id.nullable(),
  title: trimmedText(ACTION_TITLE_MAX_LENGTH),
  description: z.string().trim().max(ACTION_DESCRIPTION_MAX_LENGTH).default(''),
  assigneeUserId: id.nullable(),
  dueDate: z.string().datetime().nullable(),
  topicIds: z.array(id).max(10),
  linkedCommentIds: z.array(id).max(50),
});

export const deleteActionItemSchema = z.object({ actionItemId: id });

export const setActionStatusSchema = z.object({
  actionItemId: id,
  status: z.enum(ACTION_STATUSES),
});

export const submitRotiSchema = z.object({
  score: z.number().int().min(1).max(5),
  comment: z.string().trim().max(ROTI_COMMENT_MAX_LENGTH).default(''),
});

export type CreateCommentInput = z.infer<typeof createCommentSchema>;
export type SetVoteInput = z.infer<typeof setVoteSchema>;
export type UpsertActionItemInput = z.infer<typeof upsertActionItemSchema>;
